import React, { useRef } from "react";
import {
    Card, CardBody, CardHeader, Form, FormGroup, Label, Input, Button, Spinner, Alert, Row, Col
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "../../../components/Forms/PhoneInput";
import {
    ACCOUNT_TYPES,
} from "../../../constants/accountTypes";
import { useCreateAccount } from "../hooks/useCreateAccount";
import "../../../assets/css/page-common.css";
import BackButton from "../../../components/Common/BackButton";

const NAVY = "#221C5D";

const CreateAccountPage = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null); // Gizli input için ref

    // Custom Hook'tan her şeyi çekiyoruz
    const {
        form, companies, groups, districtOptions,
        loadingInit, loadingCompanies, loadingGroups, saving, importing, error,
        isSoleProp,
        handleAccountNameChange, handleAccountCodeChange, handleChange, generateAccountCode, handleSubmit, handleImportExcel, setForm
    } = useCreateAccount();

    const turkishCities = [
        "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Amasya", "Ankara", "Antalya", "Artvin",
        "Aydın", "Balıkesir", "Bilecik", "Bingöl", "Bitlis", "Bolu", "Burdur", "Bursa", "Çanakkale",
        "Çankırı", "Çorum", "Denizli", "Diyarbakır", "Edirne", "Elazığ", "Erzincan", "Erzurum",
        "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane", "Hakkâri", "Hatay", "Isparta", "Mersin",
        "İstanbul", "İzmir", "Kars", "Kastamonu", "Kayseri", "Kırklareli", "Kırşehir", "Kocaeli",
        "Konya", "Kütahya", "Malatya", "Manisa", "Kahramanmaraş", "Mardin", "Muğla", "Muş",
        "Nevşehir", "Niğde", "Ordu", "Rize", "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas",
        "Tekirdağ", "Tokat", "Trabzon", "Tunceli", "Şanlıurfa", "Uşak", "Van", "Yozgat", "Zonguldak",
        "Aksaray", "Bayburt", "Karaman", "Kırıkkale", "Batman", "Şırnak", "Bartın", "Ardahan",
        "Iğdır", "Yalova", "Karabük", "Kilis", "Osmaniye", "Düzce"
    ];

    if (loadingInit) {
        return (
            <div className="page-content text-center p-5">
                <Spinner size="lg" color="primary" />
                <h6 className="mt-3 text-muted">Yükleniyor...</h6>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content page-common">
                <div className="container-fluid">

                    {/* ── Page Header ── */}
                    <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <BackButton />
                            <div>
                                <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                    Yeni Cari/Bayi
                                </h5>
                                <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                    Cari/Bayi Oluşturma Sayfası
                                </span>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <a href="/files/ornek_cari_excel_import.xlsx" download="ornek_cari_sablonu.xlsx" className="text-decoration-none">
                                <button
                                    className="btn btn-light btn-rounded d-flex align-items-center gap-1 px-3"
                                    style={{ height: 34, fontSize: ".8rem" }}
                                    type="button"
                                >
                                    <i className="bx bx-download" style={{ fontSize: 16 }} /> Örnek Excel
                                </button>
                            </a>
                            <input
                                type="file"
                                accept=".xlsx, .xls"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                onChange={handleImportExcel}
                            />
                            <button
                                className="btn btn-rounded d-flex align-items-center gap-1 px-3"
                                style={{ height: 34, fontSize: ".8rem", background: "#71C30B", borderColor: "#71C30B", color: "#fff" }}
                                onClick={() => fileInputRef.current.click()}
                                disabled={importing || saving}
                            >
                                {importing ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <><i className="bx bx-import" style={{ fontSize: 16 }} /> Excel'den Ekle</>
                                )}
                            </button>
                            <button
                                className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
                                style={{ height: 34, fontSize: ".8rem" }}
                                onClick={handleSubmit}
                                disabled={saving}
                            >
                                {saving ? (<><Spinner size="sm" className="me-1" /> Kaydediliyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Oluştur</>)}
                            </button>
                        </div>
                    </div>

                    <Row>
                        <Col lg={12}>
                            <Card className="detail-card">
                                <CardHeader>
                                    <div className="section-title">
                                        <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                                            <i className="bx bx-plus-circle" />
                                        </div>
                                        Cari/Bayi Bilgileri
                                    </div>
                                </CardHeader>

                                <CardBody className="p-4">
                                    {error && (
                                        <Alert color="danger" className="border-0 rounded-4">
                                            <i className="bx bx-error me-2"></i>
                                            {error}
                                        </Alert>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        {/* Tip & Durum */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                            <h6 className="mb-3 fw-semibold">Cari/Bayi Tipi ve Durum</h6>
                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Cari/Bayi Tipi</Label>
                                                        <Input type="select" name="accountType" value={form.accountType} onChange={handleChange} className="rounded-3">
                                                            {ACCOUNT_TYPES.map((t) => (
                                                                <option key={t.value} value={t.value}>{t.label}</option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Hesap Durumu</Label>
                                                        <div className="d-flex align-items-center">
                                                            <div className="toggle-switch" onClick={() => setForm((prev) => ({ ...prev, enabled: !prev.enabled }))} style={{ width: "50px", height: "24px", backgroundColor: form.enabled ? "#28a745" : "#6c757d", borderRadius: "12px", position: "relative", cursor: "pointer", transition: "background-color 0.2s" }}>
                                                                <div style={{ width: "20px", height: "20px", backgroundColor: "white", borderRadius: "50%", position: "absolute", top: "2px", left: form.enabled ? "28px" : "2px", transition: "left 0.2s", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }} />
                                                            </div>
                                                            <span className="ms-3"><strong className="d-block">{form.enabled ? "Aktif" : "Pasif"}</strong></span>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Temel Bilgiler */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                            <h6 className="mb-3 fw-semibold">Temel Bilgiler</h6>
                                            <Row className="g-3">
                                                <Col md={8}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Cari/Bayi Adı <span className="text-danger">*</span></Label>
                                                        <Input name="accountName" value={form.accountName} onChange={handleAccountNameChange} maxLength={150} className="rounded-3" placeholder="Cari/Bayi hesap adını girin" />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Cari/Bayi Kodu <span className="text-danger">*</span></Label>
                                                        <div className="d-flex gap-2">
                                                            <Input type="text" name="accountCode" value={form.accountCode} onChange={handleAccountCodeChange} maxLength={6} inputMode="numeric" pattern="\d{6}" className="rounded-3" placeholder="6 haneli kod" />
                                                            <Button type="button" color="outline-secondary" size="sm" onClick={generateAccountCode} className="rounded-pill"><i className="bx bx-refresh"></i></Button>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Firma <span className="text-danger">*</span></Label>
                                                        <Input type="select" name="companyId" value={form.companyId} onChange={handleChange} className="rounded-3" disabled={loadingCompanies}>
                                                            <option value="">Firma seçiniz</option>
                                                            {companies.map((c) => (<option key={c.id} value={c.id}>{c.companyName}</option>))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Taksit Grubu</Label>
                                                        <Input type="select" name="accountGroupId" value={form.accountGroupId} onChange={handleChange} className="rounded-3" disabled={!form.companyId || loadingGroups}>
                                                            <option value="">Grup seçiniz</option>
                                                            {groups.map((g) => (<option key={g.id} value={g.id}>{g.groupName}</option>))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Kimlik & Vergi (Kısaltıldı, mantık aynı) */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                            <Row className="g-3">
                                                {isSoleProp ? (
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">TC Kimlik No</Label>
                                                            <Input name="nationalId" value={form.nationalId} onChange={handleChange} maxLength={11} className="rounded-3" />
                                                        </FormGroup>
                                                    </Col>
                                                ) : (
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">VKN</Label>
                                                            <Input name="vknNumber" value={form.vknNumber} onChange={handleChange} maxLength={10} className="rounded-3" />
                                                        </FormGroup>
                                                    </Col>
                                                )}
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Vergi Dairesi</Label>
                                                        <Input name="taxOffice" value={form.taxOffice} onChange={handleChange} className="rounded-3" />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* İletişim & Adres */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <PhoneInput value={form.phoneNumber} onChange={handleChange} name="phoneNumber" />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label>Şehir</Label>
                                                        <Input type="select" name="city" value={form.city} onChange={handleChange} className="rounded-3">
                                                            <option value="">Seçiniz</option>
                                                            {turkishCities.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label>İlçe</Label>
                                                        <Input type="select" name="district" value={form.district} onChange={handleChange} className="rounded-3" disabled={!form.city}>
                                                            <option value="">Seçiniz</option>
                                                            {districtOptions.map(d => <option key={d} value={d}>{d}</option>)}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label>Adres</Label>
                                                        <Input type="textarea" name="address" value={form.address} onChange={handleChange} className="rounded-3" />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Footer Buttons */}
                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <button type="button" className="btn btn-light btn-rounded px-4" style={{ height: 34, fontSize: ".8rem" }} onClick={() => navigate(-1)} disabled={saving}>
                                                İptal
                                            </button>
                                            <button type="submit" className="btn btn-navy btn-rounded px-4 d-flex align-items-center gap-1" style={{ height: 34, fontSize: ".8rem" }} disabled={saving}>
                                                {saving ? (<><Spinner size="sm" className="me-1" /> Kaydediliyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Oluştur</>)}
                                            </button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default CreateAccountPage;